import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Typography } from "src/UILibrary"
import { useRecoilState } from "recoil"
import Stepper from "src/components/atom/Stepper"
import { GENDER, MEMBER_TYPE, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { ShowToast } from "src/states/showToast"
import { AuthenticationService, ProfileService } from "src/openapi"
import { AddressService } from "src/openapi_address"
import { Dayjs } from "dayjs"
import {
  validateCompanyInformation,
  validateGeneralInformation,
  validateLoginInformation,
} from "../UserValidation"
import { UserGeneralInformation } from "./UserGeneralInformation"
import { UserCompanyInformation } from "./UserCompanyInformation"
import { UserLoginInformation } from "./UserLoginInformation"
import { ShowHeader } from "src/states/showHeader"
import { useNavigate } from "react-router-dom"
import { useMediaQuery } from "react-responsive"
import { ShowProgress } from "src/states/showProgress"

const STEP = {
  GENERAL_INFORMATION: 1,
  LOGIN_INFORMATION: 2,
  COMPANY_INFORMATION: 3,
}

export const UserRegisterPage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const [, setShowHeader] = useRecoilState(ShowHeader)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [step, setStep] = useState<number>(STEP.GENERAL_INFORMATION)

  const navigate = useNavigate()

  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [firstNameJP, setFirstNameJP] = useState<string>("")
  const [lastNameJP, setLastNameJP] = useState<string>("")
  const [birthday, setBirthday] = useState<Dayjs | null>(null)
  const [gender, setGender] = useState<string>(GENDER.MALE)
  const [nation, setNation] = useState<string>("日本")
  const [areaId, setAreaId] = useState<number>(1)
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [postCode, setPostCode] = useState<string>("")
  const [province, setProvince] = useState<string>("北海道")
  const [city, setCity] = useState<string>("")
  const [street, setStreet] = useState<string>("")
  const [building, setBuilding] = useState<string>("")
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")
  const [birthdayValidationMessage, setBirthdayValidationMessage] = useState<string>("")
  const [phoneNumberValidationMessage, setPhoneNumberValidationMessage] = useState<string>("")
  const [postCodeValidationMessage, setPostCodeValidationMessage] = useState<string>("")
  const [cityValidationMessage, setCityValidationMessage] = useState<string>("")

  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [passwordConfirm, setPasswordConfirm] = useState<string>("")
  const [magazineRegister, setMagazinRegister] = useState<boolean>()
  const [startStageID, setStartStageID] = useState<number>()
  const [startPlanID, setStartPlanID] = useState<number>()
  const [emailValidationMessage, setEmailValidationMessage] = useState<string>("")
  const [passwordValidationMessage, setPasswordValidationMessage] = useState<string>("")
  const [passwordConfirmValidationMessage, setPasswordConfirmValidationMessage] =
    useState<string>("")
  const [magazinValidationMessage, setMagazinValidationMessage] = useState<string>("")
  const [startStageIDValidationMessage, setStartStageIDValidationMessage] = useState<string>("")
  const [startPlanIDValidationMessage, setStartPlanIDValidationMessage] = useState<string>("")

  const [companyName, setCompanyName] = useState<string>("")
  const [companyNameJP, setCompanyNameJP] = useState<string>("")
  const [startDate, setStartDate] = useState<Dayjs | null>(null)
  const [siteURL, setSiteURL] = useState<string>("")
  const [route, setRoute] = useState<number>()
  const [routeComment, setRouteComment] = useState<string>("")
  const [companyType, setCompanyType] = useState<number>()
  const [startDateValidationMessage, setStartDateValidationMessage] = useState<string>("")
  const [siteURLValidationMessage, setSiteURLValidationMessage] = useState<string>("")
  const [routeValidationMessage, setRouteValidationMessage] = useState<string>("")
  const [companyTypeValidationMessage, setCompanyTypeValidationMessage] = useState<string>("")

  const [lounge, setLounge] = useState<boolean>(false)
  const [loungeValidationMessage, setLoungeValidationMessage] = useState<string>("")

  useEffect(() => {
    setShowHeader(false)
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  const handlePostCodeInput = () => {
    if (!postCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(postCode).then((res) => {
      if (res.prefecture) {
        setProvince(res.prefecture as string)
      }

      if (res.city) {
        let city = res.city as string
        if (res.townCandidates && res.townCandidates.length >= 1) {
          city = `${city}${res.townCandidates[0]}`
        }
        setCity(city)
      }
    })
  }

  const confirmGeneralInformation = async () => {
    if (
      !(await validateGeneralInformation(
        firstName,
        lastName,
        firstNameJP,
        lastNameJP,
        birthday ? birthday?.format("YYYY-MM-DD") : "",
        phoneNumber,
        postCode,
        city,
        setNameValidationMessage,
        setBirthdayValidationMessage,
        setPhoneNumberValidationMessage,
        setPostCodeValidationMessage,
        setCityValidationMessage,
        t
      ))
    ) {
      return
    }

    setStep(STEP.LOGIN_INFORMATION)
  }

  const confirmLoginInformation = async () => {
    if (
      !(await validateLoginInformation(
        email,
        password,
        passwordConfirm,
        setEmailValidationMessage,
        setPasswordValidationMessage,
        setPasswordConfirmValidationMessage,
        magazineRegister,
        setMagazinValidationMessage,
        startStageID,
        setStartStageIDValidationMessage,
        startPlanID,
        setStartPlanIDValidationMessage,
        t
      ))
    ) {
      return
    }

    setStep(STEP.COMPANY_INFORMATION)
  }

  const handleRegister = async () => {
    if (
      !(await validateCompanyInformation(
        startDate ? startDate.format("YYYY-MM-DD") : "",
        setStartDateValidationMessage,
        siteURL,
        setSiteURLValidationMessage,
        route,
        routeComment,
        setRouteValidationMessage,
        companyType,
        setCompanyTypeValidationMessage,
        lounge,
        setLoungeValidationMessage,
        t
      ))
    ) {
      return
    }

    setShowProgress(true)
    AuthenticationService.signUp({
      email: email,
      password: password,
      userType: MEMBER_TYPE.USER,
    })
      .then((res) => {
        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          ProfileService.createMember({
            userId: res.data?.UserSub as string,
            email: email,
            firstName: firstName,
            lastName: lastName,
            firstNameKana: firstNameJP,
            lastNameKana: lastNameJP,
            gender: gender,
            birthday: birthday?.format("YYYY-MM-DD") as string,
            country: nation,
            areaId: areaId,
            phoneNumber: phoneNumber,
            zipCode: postCode,
            province: province,
            city: city,
            streetNo: street,
            building: building,
            isMagazinRegister: magazineRegister,
            startupStageId: startStageID as number,
            startupPlanId: startPlanID as number,
            startupName: companyName,
            startupKanaName: companyNameJP,
            startupOpenDate: startDate?.isValid() ? startDate.format("YYYY-MM-DD") : undefined,
            startupUrl: siteURL,
            routeToKnowId: route as number,
            routeComment: routeComment,
            startupBusinessTypeId: companyType as number,
            isLoungeMember: lounge,
            comment: "USER_SIGNUP",
          })
            .then((res) => {
              setShowProgress(false)
              if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
                setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
                navigate("/mypage/users/list/member")
                navigate(0)
              } else {
                setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
              }
            })
            .catch(() => {
              setShowProgress(false)
              setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
            })
        } else if (res.status === RESPONSE_CODE.CONFLICT) {
          setShowProgress(false)
          setShowToast({ show: true, text: t("already_registered"), type: SNACKBAR_TYPES.WARNING })
        } else {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "auto", pt: "0px", pl: "20px" }
          : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <Box sx={{ px: "20px", pt: "20px" }}>
        <Typography.Large
          color="textPrimary"
          sx={{
            mb: "40px",
          }}
        >
          {t("signup_general_user", { step: step })}
        </Typography.Large>
        <Stepper
          steps={[t("general_user_step_1"), t("general_user_step_2"), t("general_user_step_3")]}
          current={step}
          sx={{ mb: "60px" }}
        ></Stepper>
        {step === STEP.GENERAL_INFORMATION && (
          <UserGeneralInformation
            firstName={firstName}
            lastName={lastName}
            firstNameJP={firstNameJP}
            lastNameJP={lastNameJP}
            handleFirstName={setFirstName}
            handleLastName={setLastName}
            handleFirstNameJP={setFirstNameJP}
            handleLastNameJP={setLastNameJP}
            birthday={birthday}
            handleBirthday={setBirthday}
            gender={gender}
            handleGener={setGender}
            nation={nation}
            handleNation={setNation}
            areaId={areaId}
            handleAreaId={setAreaId}
            phoneNumber={phoneNumber}
            handlePhoneNumber={setPhoneNumber}
            postCode={postCode}
            handlePostCode={setPostCode}
            handlePostCodeInput={handlePostCodeInput}
            province={province}
            handleProvince={setProvince}
            city={city}
            handleCity={setCity}
            street={street}
            handleStreet={setStreet}
            building={building}
            handleBuilding={setBuilding}
            nameValidationMessage={nameValidationMessage}
            birthdayValidationMessage={birthdayValidationMessage}
            phoneNumberValidationMessage={phoneNumberValidationMessage}
            postCodeValidationMessage={postCodeValidationMessage}
            cityValidationMessage={cityValidationMessage}
            handleNext={() => confirmGeneralInformation()}
          />
        )}
        {step === STEP.LOGIN_INFORMATION && (
          <UserLoginInformation
            email={email}
            password={password}
            passwordConfirm={passwordConfirm}
            handleEmail={setEmail}
            handlePassword={setPassword}
            handlePasswordConfirm={setPasswordConfirm}
            magazinRegister={magazineRegister}
            handleMagazinRegister={setMagazinRegister}
            startStageID={startStageID}
            handleStartStageID={setStartStageID}
            startPlanID={startPlanID}
            handleStartPlanID={setStartPlanID}
            emailValidationMessage={emailValidationMessage}
            passwordValidationMessage={passwordValidationMessage}
            passwordConfirmValidationMessage={passwordConfirmValidationMessage}
            magazinValidationMessage={magazinValidationMessage}
            startStageIDValidationMessage={startStageIDValidationMessage}
            startPlanIDValidationMessage={startPlanIDValidationMessage}
            handlePrev={() => setStep(STEP.GENERAL_INFORMATION)}
            handleNext={() => confirmLoginInformation()}
          />
        )}
        {step === STEP.COMPANY_INFORMATION && (
          <UserCompanyInformation
            companyName={companyName}
            handleCompanyName={setCompanyName}
            companyNameJP={companyNameJP}
            handleCompanyNameJP={setCompanyNameJP}
            startDate={startDate}
            handleStartDate={setStartDate}
            startDateValidationMessage={startDateValidationMessage}
            siteURL={siteURL}
            handleSiteURL={setSiteURL}
            siteURLValidationMessage={siteURLValidationMessage}
            route={route}
            handleRoute={setRoute}
            routeComment={routeComment}
            handleRouteComment={setRouteComment}
            routeValidationMessage={routeValidationMessage}
            companyType={companyType}
            handleCompanyType={setCompanyType}
            companyTypeValidationMessage={companyTypeValidationMessage}
            lounge={lounge}
            handleLounge={setLounge}
            loungeValidationMessage={loungeValidationMessage}
            handlePrev={() => setStep(STEP.LOGIN_INFORMATION)}
            handleRegister={() => handleRegister()}
          />
        )}
      </Box>
    </Box>
  )
}
